"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface ConfirmOptions {
  /** 進めるボタンの文言。既定は 決定 */
  ok?: string;
  /** やめるボタンの文言。既定は キャンセル */
  cancel?: string;
  /** 捨てる・消す確認は true。進めるボタンを danger の見た目にする */
  danger?: boolean;
}

interface Asking extends ConfirmOptions {
  message: string;
}

/** 確認のダイアログの中身。画面の中央に重ねて出す */
function ConfirmDialog({ asking, onAnswer }: { asking: Asking; onAnswer: (yes: boolean) => void }) {
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    okRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onAnswer(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onAnswer]);

  return (
    <div className="dialog-backdrop" onClick={() => onAnswer(false)}>
      <div className="dialog" role="alertdialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <p className="dialog-message">{asking.message}</p>
        <div className="dialog-actions">
          <button
            ref={okRef}
            type="button"
            className={asking.danger ? "button button-danger" : "button button-primary"}
            onClick={() => onAnswer(true)}
          >
            {asking.ok ?? "決定"}
          </button>
          <button type="button" className="button" onClick={() => onAnswer(false)}>
            {asking.cancel ?? "キャンセル"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 確認を画面の中のダイアログで取る。ブラウザのダイアログ（confirm・alert・prompt）は使わない。
 * 返す 2 つは、確認を出す関数と、画面のどこかに置くダイアログの要素。
 */
export function useConfirm(): [(message: string, options?: ConfirmOptions) => Promise<boolean>, React.ReactNode] {
  const [asking, setAsking] = useState<Asking | null>(null);
  const resolver = useRef<((yes: boolean) => void) | null>(null);

  const ask = useCallback((message: string, options?: ConfirmOptions) => {
    resolver.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setAsking({ message, ...options });
    });
  }, []);

  const answer = useCallback((yes: boolean) => {
    resolver.current?.(yes);
    resolver.current = null;
    setAsking(null);
  }, []);

  return [ask, asking ? <ConfirmDialog asking={asking} onAnswer={answer} /> : null];
}
